import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import coursesData from './courses.json';
import { useUser } from '../context/UserContext';
import glossary from '../glossary.json';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import './course-experience.css';

// Shared card style for course tiles
const cardStyle = {
  height: 'calc(100% - 20px)',
  flex: 1,
  maxWidth: 400,
  borderRadius: 18,
  backgroundColor: '#5932ea',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
  backgroundImage:
    'linear-gradient(15deg, rgba(255, 255, 255, 0.31), rgba(0, 0, 0, 0) 40%)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-start',
  padding: 10,
  fontSize: 24,
  color: 'white',
  fontWeight: 'normal',
  cursor: 'pointer',
};

// Reusable CourseCard component that navigates by course ID
const CourseCard = ({ id, title, description, level, lessonCount, featured }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="learnCourseCard"
      style={{
        ...cardStyle,
        border: 0,
        textAlign: 'left',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: 22,
        background: featured
          ? 'linear-gradient(145deg, #221842, #6d4aff)'
          : 'linear-gradient(145deg, #5932ea, #8066ea)',
        transition: 'transform .2s ease, box-shadow .2s ease',
      }}
      onClick={() => navigate(`/courses/${id}`)}
      onMouseEnter={(event) => { event.currentTarget.style.transform = 'translateY(-5px)'; }}
      onMouseLeave={(event) => { event.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <span style={{ fontSize: 11, padding: '5px 8px', borderRadius: 999, background: 'rgba(255,255,255,.16)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
        {featured ? 'Interactive example' : level || 'Self-paced'}
      </span>
      <span>
        <strong style={{ display: 'block', fontSize: 23, lineHeight: 1.2 }}>{title}</strong>
        {description && <span style={{ display: 'block', fontSize: 12, lineHeight: 1.5, opacity: .78, marginTop: 8, fontWeight: 400 }}>{description}</span>}
        <span style={{ display: 'block', fontSize: 11, marginTop: 12, opacity: .72 }}>{lessonCount} lessons · Start course →</span>
      </span>
    </button>
  );
};
const Learn = () => {
  const [view, setView] = useState('courses'); // "courses" | "glossary"
  const { user } = useUser();
  const language = user?.language || 'en';
  const glossaryItems = useMemo(() => {
    const termsByName = new Map();

    glossary.forEach((item) => {
      const current = termsByName.get(item.term);

      if (!current) {
        termsByName.set(item.term, { ...item, id: item.term });
        return;
      }

      termsByName.set(item.term, {
        ...current,
        definition:
          item.definition.length > current.definition.length
            ? item.definition
            : current.definition,
        formula: current.formula || item.formula,
        display: current.display || item.display,
      });
    });

    return Array.from(termsByName.values()).sort((a, b) =>
      a.term.localeCompare(b.term)
    );
  }, []);
  const [glossarySearch, setGlossarySearch] = useState('');
  const [activeLetter, setActiveLetter] = useState('All');
  const [expandedGlossaryId, setExpandedGlossaryId] = useState('');
  const glossaryLetters = useMemo(
    () => Array.from(new Set(glossaryItems.map(item => item.term.charAt(0).toUpperCase()))),
    [glossaryItems]
  );
  const filteredGlossaryItems = useMemo(() => {
    const query = glossarySearch.trim().toLowerCase();
    return glossaryItems.filter(item => {
      const matchesLetter =
        activeLetter === 'All' || item.term.charAt(0).toUpperCase() === activeLetter;
      const matchesSearch =
        !query ||
        item.term.toLowerCase().includes(query) ||
        item.definition.toLowerCase().includes(query);
      return matchesLetter && matchesSearch;
    });
  }, [activeLetter, glossaryItems, glossarySearch]);
  const glossaryGroups = useMemo(
    () => filteredGlossaryItems.reduce((groups, item) => {
      const letter = item.term.charAt(0).toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(item);
      return groups;
    }, {}),
    [filteredGlossaryItems]
  );
  // pull the full list of courses from your JSON
  const allCourses = coursesData.courses.map((c) => ({
    id: c.id,
    title: c.title,
    description: c.description,
    level: c.level,
    lessonCount: c.sections.reduce((total, section) => total + section.lessons.length, 0),
  }));

  const sections = [
    { title: 'Learn StockSleuth', items: allCourses },
  ];

  return (
    <div
      className="learnPage"
      style={{
        position: 'absolute',
        top: 64,
        right: "var(--sidebar-width, 306px)",
        bottom: 0,
        left: 0,
        overflowY: 'auto',
      }}
    >
      {/* toggle buttons */}
      <div
        className="body-RangeSelector mobileRangeSelector"
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0)',
          boxShadow: 'none',
          gap: 14,
          padding: '10px 30px',
        }}
      >
        <button
          className={`range-button ${view === 'courses' ? 'active' : ''}`}
          onClick={() => setView('courses')}
        >
          Courses
        </button>
        <button
          className={`range-button ${view === 'glossary' ? 'active' : ''}`}
          onClick={() => setView('glossary')}
        >
          Glossary
        </button>
      </div>

      {/* conditional rendering */}
      {view === 'courses' ? (
        sections.map((section) => (
          <div key={section.title} style={{ marginBottom: 40 }}>
            <h1 className="learnSectionTitle" style={{ textAlign: 'left', marginLeft: 20 }}>
              {section.title}
            </h1>
            <div
              className="learnCourseRow"
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 30,
                width: 'calc(100% - 40px)',
                height: 240,
                padding: '0 20px',
              }}
            >
              {section.items.map((course) => (
                <CourseCard
                  key={course.id}
                  {...course}
                  featured={course.id === 'intro-to-stocksleuth'}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="glossaryDirectory">
          <header className="glossaryDirectoryHeader">
            <div>
              <span className="glossaryEyebrow">Investor reference</span>
              <h1>
                {language === 'en'
                  ? 'Financial glossary'
                  : language === 'pl'
                  ? 'Slowniczek finansowy'
                  : 'Glossaire financier'}
              </h1>
              <p>Browse {glossaryItems.length} investing terms, metrics, and formulas.</p>
            </div>
            <label className="glossarySearch">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m21 21-4.35-4.35m2.35-5.15A7.5 7.5 0 1 1 4 11.5a7.5 7.5 0 0 1 15 0Z" />
              </svg>
              <input
                value={glossarySearch}
                onChange={event => {
                  setGlossarySearch(event.target.value);
                  setActiveLetter('All');
                }}
                placeholder="Search terms or definitions"
                type="search"
              />
            </label>
          </header>

          <nav className="glossaryAlphabet" aria-label="Filter glossary by letter">
            {['All', ...glossaryLetters].map(letter => (
              <button
                key={letter}
                type="button"
                className={activeLetter === letter ? 'active' : ''}
                onClick={() => setActiveLetter(letter)}
              >
                {letter}
              </button>
            ))}
          </nav>

          <div className="glossaryResultMeta">
            {filteredGlossaryItems.length} {filteredGlossaryItems.length === 1 ? 'term' : 'terms'}
            {glossarySearch && <> matching “{glossarySearch}”</>}
          </div>

          {filteredGlossaryItems.length ? (
            <div className="glossaryGroups">
              {Object.entries(glossaryGroups).map(([letter, items]) => (
                <section className="glossaryGroup" key={letter} id={`glossary-${letter}`}>
                  <div className="glossaryLetter">{letter}</div>
                  <div className="glossaryTermGrid">
                    {items.map(item => {
                      const isExpanded = expandedGlossaryId === item.id;
                      return (
                        <article className={`glossaryCard ${isExpanded ? 'expanded' : ''}`} key={item.id}>
                          <button
                            type="button"
                            className="glossaryCardButton"
                            aria-expanded={isExpanded}
                            onClick={() => setExpandedGlossaryId(isExpanded ? '' : item.id)}
                          >
                            <span>{item.term}</span>
                            <span className="glossaryExpandIcon">{isExpanded ? '−' : '+'}</span>
                          </button>
                          {isExpanded && (
                            <div className="glossaryCardDetail">
                              {item.formula && (
                                <div className="glossaryFormula">
                                  {item.display === 'block'
                                    ? <BlockMath math={item.formula} />
                                    : <InlineMath math={item.formula} />}
                                </div>
                              )}
                              <p>{item.definition}</p>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="glossaryEmpty">
              <strong>No terms found</strong>
              <span>Try another keyword or select “All”.</span>
              <button type="button" onClick={() => { setGlossarySearch(''); setActiveLetter('All'); }}>
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Learn;
